import React, { MouseEvent } from 'react';
import NewButtonGroup from './NewButtonGroup';
import ThumbUpLineIcon from '../../atoms/icons/ThumbUpLineIcon';
import ThumbDownLineIcon from '../../atoms/icons/ThumbDownLineIcon';
import PauseCircleLineIcon from '../../atoms/icons/PauseCircleLineIcon';
import MailCloseLineIcon from '../../atoms/icons/MailCloseLineIcon';
import MailLineIcon from '../../atoms/icons/MailLineIcon';
import SurveyLineIcon from '../../atoms/icons/SurveyLineIcon';
import { useTranslation } from 'react-i18next';
import CalendarIcon from '../../atoms/icons/CalendarIcon';
import ContactsLineIcon from '../../atoms/icons/ContactsLineIcon';
import CandidateFilterStatus from '../../../enums/candidateFilterStatus';
import CandidateScreeningRequestStatusEnum from '../../../enums/candidateScreeningRequestStatusEnum'
import UserUnfollowLineIcon from '../../atoms/icons/UserUnfollowLineIcon';
import UserStarLineIcon from '../../atoms/icons/UserStarLineIcon'
import { useFeature } from '@growthbook/growthbook-react';

interface CandidateTableButtonGroupProps {
    status: string;
    hasCotenants: boolean;
    hasScreeningOutcome: boolean;
    isListingTenant: boolean;
    screeningRequestStatus: string;
    hasCoTenantsWithInvitedScreeningRequests: boolean;
    invitedToViewingPending: boolean;
    hasDeclinedInviteToViewing: boolean;
    hasAcceptedInviteToViewing: boolean;
    hasFilledExtraInfo: boolean;
    hasReceivedExtraInfo: boolean;
    onApprove: (e?: MouseEvent) => void;
    onUnassignTenant: (e?: MouseEvent) => void;
    onRejectWithMessage: (e?: MouseEvent) => void;
    onMoveToWaitingList: (e?: MouseEvent) => void;
    onRejectWithoutMessage: (e?: MouseEvent) => void;
    onSendMessage: (e?: MouseEvent) => void;
    onReqestExtraInfo: (e?: MouseEvent) => void;
    onInviteToViewing: (e?: MouseEvent) => void;
    onScreeningRequest: (e?: MouseEvent) => void;
    onAssignTenant: (e?: MouseEvent) => void;
}

const CandidateTableButtonGroup = ({
    status,
    isListingTenant,
    invitedToViewingPending,
    screeningRequestStatus,
    onApprove,
    onUnassignTenant,
    onRejectWithMessage,
    onMoveToWaitingList,
    onRejectWithoutMessage,
    onSendMessage,
    onReqestExtraInfo,
    onInviteToViewing,
    onScreeningRequest,
    onAssignTenant,
}: CandidateTableButtonGroupProps) => {
    const { t } = useTranslation();
    const FEATURE_FLAG_PROPERIZE_IS_UNAVAILABLE = useFeature('properize-is-unavailable').value;
    console.log('FEATURE_FLAG_PROPERIZE_IS_UNAVAILABLE', FEATURE_FLAG_PROPERIZE_IS_UNAVAILABLE);

    const availableButtons = {
        ScreeningButton: {
            text: t('candidate.action.screen'),
            variant: 'secondary-gray',
            icon: <ContactsLineIcon />,
            onClick: onScreeningRequest,
            disabled: FEATURE_FLAG_PROPERIZE_IS_UNAVAILABLE,
        },
        AssignTenantButton: {
            text: t('candidate.action.assignTenant'),
            variant: 'secondary-gray',
            icon: <UserStarLineIcon />,
            onClick: onAssignTenant
        },
        UnassignTenantButton: {
            text: t('candidate.action.unassign'),
            variant: 'secondary-gray',
            icon: <UserUnfollowLineIcon/>,
            onClick: onUnassignTenant
        },
        ApproveButton: {
            text: t('candidate.action.approve'),
            variant: 'secondary-gray',
            icon: <ThumbUpLineIcon />,
            onClick: onApprove
        },
        MoveToWaitingListButton: {
            text: t('bulk.dropdown.queue'),
            variant: 'secondary-gray',
            icon: <PauseCircleLineIcon />,
            onClick: onMoveToWaitingList
        },
        InviteToViewingButton: {
            text: t('candidate.action.invite'),
            variant: 'secondary-gray',
            icon: <CalendarIcon />,
            onClick: onInviteToViewing
        },
        RejectWithMessageButton: {
            text: t('candidate.action.reject'),
            variant: 'secondary-gray',
            icon: <ThumbDownLineIcon />,
            onClick: onRejectWithMessage
        },
        RejectWithoutMessageButton: {
            text: t('candidate.action.reject.withoutMessage'),
            variant: 'secondary-gray',
            icon: <MailCloseLineIcon />,
            onClick: onRejectWithoutMessage
        },
        SendMessageButton: {
            text: t('candidate.action.initiateMessage'),
            variant: 'secondary-gray',
            icon: <MailLineIcon />,
            onClick: onSendMessage
        },
        RequestExtraInfoButton: {
            text: t('bulk.dropdown.extra.info'),
            variant: 'secondary-gray',
            icon: <SurveyLineIcon />,
            onClick: onReqestExtraInfo
        }
    }

    const renderButtons = (buttons, newButton?) => {
        let ret = buttons;

        if (isListingTenant) {
            ret = [ ...buttons, availableButtons.UnassignTenantButton ];
        } else if (newButton) {
            ret = [ ...buttons, newButton ];
        }

        return ret;
    }

    let buttons = [];
    switch (status) {
        case CandidateFilterStatus.None:
            buttons = [
                availableButtons.RequestExtraInfoButton,
            ];
            break;
        case CandidateFilterStatus.Assessment:
        case CandidateFilterStatus.NotSuitable:
            buttons = [
                availableButtons.ApproveButton,
                availableButtons.RejectWithMessageButton,
            ];
            break;
        case CandidateFilterStatus.Suitable:
            if (screeningRequestStatus === CandidateScreeningRequestStatusEnum.Invited && invitedToViewingPending) {
                buttons = [availableButtons.SendMessageButton];
            } else if (invitedToViewingPending) {
                buttons = [availableButtons.ScreeningButton];
            } else {
                buttons = [availableButtons.InviteToViewingButton];
            }
            break;
        case CandidateFilterStatus.Rejected:
            buttons = [
                availableButtons.SendMessageButton,
            ];
            break;
    }

    let dropdownButtons = [];
    switch (status) {
        case CandidateFilterStatus.None:
            dropdownButtons = [
                availableButtons.ApproveButton,
                availableButtons.MoveToWaitingListButton,
                availableButtons.RejectWithMessageButton,
                availableButtons.RejectWithoutMessageButton,
                availableButtons.SendMessageButton,
            ];

            break;
        case CandidateFilterStatus.Assessment:
            dropdownButtons = [
                availableButtons.MoveToWaitingListButton,
                availableButtons.RejectWithoutMessageButton,
                availableButtons.SendMessageButton,
            ];
            break;
        case CandidateFilterStatus.NotSuitable:
            dropdownButtons = [
                availableButtons.RejectWithoutMessageButton,
                availableButtons.SendMessageButton,
            ];
            break;
        case CandidateFilterStatus.Suitable:
            dropdownButtons = [
                availableButtons.AssignTenantButton,
                availableButtons.MoveToWaitingListButton,
                availableButtons.RejectWithMessageButton,
                availableButtons.RejectWithoutMessageButton,
                availableButtons.SendMessageButton,
            ];

            if (invitedToViewingPending || screeningRequestStatus === CandidateScreeningRequestStatusEnum.Invited) {
                const moveToWaitingListIndex = dropdownButtons.findIndex(
                    button => button === availableButtons.MoveToWaitingListButton
                );
                dropdownButtons.splice(moveToWaitingListIndex + 1, 0, availableButtons.InviteToViewingButton);
            } else {
                dropdownButtons.unshift(availableButtons.ScreeningButton);
            }
            break;
        case CandidateFilterStatus.Rejected:
            dropdownButtons = [
                availableButtons.ApproveButton,
                availableButtons.MoveToWaitingListButton,
            ];

            break;

    }

    if (!buttons.length && !dropdownButtons.length) {
        return null;
    }

    return (
        <NewButtonGroup
            buttons={buttons}
            dropdownButtons={renderButtons(dropdownButtons)}
            align='right'
        />
    );
}

export default CandidateTableButtonGroup;
